/**
 * MissionTranscriptSheet — read-only transcript of a catch-up mission for the
 * teacher. Opens as a right-side Sheet from either the NeedsHelpFeedCard's
 * "View" action or from the teacher's "View Chats" page conv list.
 *
 * Props:
 *   - open: boolean
 *   - onOpenChange: fn(boolean)
 *   - missionId: string
 *   - brandColor?: string (optional — defaults to slate)
 *
 * Data:
 *   GET /api/teacher/me/missions/{mission_id}/transcript
 *   -> { mission: {...}, messages: [...], not_started: bool }
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Loader2, MessageCircle, Sparkles, AlertCircle } from 'lucide-react';
import RichChatMessage from '@/components/chat/RichChatMessage';

const SIGNAL_LABEL = {
  auto_hard_cap: 'Hard cap reached',
  student_confused: 'Student said confused',
  final_judge_no: 'Final judge — no',
  request_closure: 'Student requested closure',
};

function fmtTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
}

export default function MissionTranscriptSheet({
  open, onOpenChange, missionId, brandColor = '#0f172a',
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !missionId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    api.get(`/teacher/me/missions/${missionId}/transcript`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || 'Unable to load transcript');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, missionId]);

  const mission = data?.mission || {};
  const messages = data?.messages || [];
  const notStarted = !!data?.not_started;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl flex flex-col gap-0 p-0"
        data-testid="mission-transcript-sheet"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-200 space-y-2">
          <SheetTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <MessageCircle size={18} className="text-amber-500" />
            Catch-up transcript
          </SheetTitle>
          <SheetDescription className="text-xs text-slate-500">
            Read-only view of the student's catch-up conversation.
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="flex-1 flex items-center justify-center text-slate-500 text-sm"
               data-testid="mission-transcript-loading">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading transcript…
          </div>
        )}

        {error && !loading && (
          <div className="flex-1 flex items-center justify-center p-6"
               data-testid="mission-transcript-error">
            <div className="flex items-start gap-2 text-rose-700 text-sm">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* Mission meta header */}
            <div className="px-6 py-4 bg-amber-50/40 border-b border-amber-100">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-semibold text-slate-900" data-testid="mission-transcript-student">
                  {mission.student_name || 'Student'}
                </span>
                {mission.student_grade && (
                  <Badge variant="outline" className="text-[10px] bg-white">
                    Grade {mission.student_grade}
                  </Badge>
                )}
              </div>
              <div className="text-sm text-slate-700 mb-1" data-testid="mission-transcript-topic">
                <Sparkles size={12} className="inline -mt-0.5 mr-1 text-amber-500" />
                {mission.concept_tag || 'Untitled topic'}
              </div>
              {mission.source_assessment_title && (
                <div className="text-[11px] text-slate-500 mb-1">
                  From: <span className="italic">{mission.source_assessment_title}</span>
                </div>
              )}
              <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                {typeof mission.exchanges_count === 'number' && (
                  <span>{mission.exchanges_count} turns</span>
                )}
                {mission.completion_signal && (
                  <span>· {SIGNAL_LABEL[mission.completion_signal] || mission.completion_signal}</span>
                )}
                {mission.completed_at && (
                  <span>· {fmtTime(mission.completed_at)}</span>
                )}
              </div>
              {mission.completion_rationale && (
                <div className="mt-2 text-xs italic text-slate-600 border-l-2 border-amber-300 pl-2"
                     data-testid="mission-transcript-rationale">
                  "{mission.completion_rationale}"
                </div>
              )}
            </div>

            {/* Body — messages */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3"
                 data-testid="mission-transcript-messages">
              {notStarted && (
                <div className="text-center text-sm text-slate-500 py-12"
                     data-testid="mission-transcript-not-started">
                  Student hasn't started this mission yet.
                </div>
              )}
              {!notStarted && messages.length === 0 && (
                <div className="text-center text-sm text-slate-500 py-12">
                  No messages in this conversation yet.
                </div>
              )}
              {messages.map((m, idx) => {
                const isStudent = m.role === 'user';
                const isYukti = m.role === 'yukti' || m.role === 'assistant';
                return (
                  <div
                    key={idx}
                    className={`flex flex-col ${isStudent ? 'items-end' : 'items-start'}`}
                    data-testid={`transcript-msg-${idx}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm break-words overflow-x-auto ${
                        isStudent
                          ? 'text-white rounded-br-sm'
                          : isYukti
                            ? 'bg-slate-100 text-slate-800 rounded-bl-sm'
                            : 'bg-slate-50 text-slate-500 italic'
                      }`}
                      style={isStudent ? { backgroundColor: brandColor } : undefined}
                    >
                      <RichChatMessage
                        text={m.text}
                        className={isStudent ? 'text-white' : ''}
                      />
                    </div>
                    <span className="text-[10px] text-slate-400 mt-1 px-1">
                      {fmtTime(m.timestamp)}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
